import { RechnungenBereich } from './types';

interface RechnungForVisibility {
  zustaendig_id?: string | null;
  zustaendig_typ?: string | null;
  objekt_id?: string | null;
  tech_pruefung_an?: string | null;
  freigabe_angefordert_an?: string | null;
  is_unklar_source?: boolean;
  objekte?: { objektnummer?: string; objekttyp?: string } | null;
}

// Gleiche Rollenliste wie die RLS-Funktion darf_alle_rechnungen_sehen() (lowercase-Vergleich).
export const ADMIN_ROLLEN = ['admin', 'super admin', 'master admin'];

interface UserForVisibility {
  id: string;
  role?: string;
  rechnungen_bereich?: RechnungenBereich;
  team_member_ids?: string[];
  team_ids?: string[];
  sieht_alle_rechnungen?: boolean;
}

export const filterSichtbareRechnungen = <T extends RechnungForVisibility>(
  rechnungen: T[],
  user: UserForVisibility
): T[] => {
  if (ADMIN_ROLLEN.includes((user.role || '').toLowerCase())) return rechnungen;

  const istEigene = (r: RechnungForVisibility): boolean =>
    r.zustaendig_id === user.id ||
    r.tech_pruefung_an === user.id ||
    r.freigabe_angefordert_an === user.id ||
    // Team: Rechnungen von Teamkollegen sind sichtbar (dauerhaft geteilt)
    (!!r.zustaendig_id && (user.team_member_ids?.includes(r.zustaendig_id) ?? false)) ||
    // Team-Zuweisung: Rechnung ist direkt einem Team des Nutzers zugewiesen (deckungsgleich mit RLS).
    (r.zustaendig_typ === 'team' && !!r.zustaendig_id && (user.team_ids?.includes(r.zustaendig_id) ?? false));

  // "Nur eigene" (Sichtbarkeit-Tab): deckungsgleich mit der RLS-Menge — kein Bereichs-Bypass.
  if (user.sieht_alle_rechnungen === false) return rechnungen.filter(istEigene);

  const bereich = user.rechnungen_bereich;
  if (bereich === 'weg' || bereich === 'mv') {
    return rechnungen.filter(r => {
      if (istEigene(r)) return true;
      if (!r.objekt_id) return true;
      const objekttyp = r.objekte?.objekttyp;
      return bereich === 'weg' ? objekttyp === 'WEG' : objekttyp !== 'WEG';
    });
  }

  // 'alle' oder kein Bereich gesetzt: kein fachlicher Cut (sieht_alle_rechnungen ist
  // true/undefined — Altbestand ohne Flag verhält sich wie „alles sehen").
  return rechnungen;
};

export const getFaelligkeitsBadge = (
  faelligkeitsdatum: string | undefined | null,
  status: string
): 'rot' | 'gelb' | null => {
  if (!faelligkeitsdatum || status?.toLowerCase() === 'bezahlt' || status?.toLowerCase() === 'klaerung') return null;
  const heute = new Date();
  heute.setHours(0, 0, 0, 0);
  const faellig = new Date(faelligkeitsdatum);
  faellig.setHours(0, 0, 0, 0);

  const diffTage = Math.ceil((faellig.getTime() - heute.getTime()) / (1000 * 60 * 60 * 24));

  if (diffTage < 0) return 'rot';
  if (diffTage <= 3) return 'gelb';
  return null;
};

export const countFaelligeRechnungen = (
  rechnungen: { faelligkeitsdatum?: string | null; status: string }[]
): number => {
  return rechnungen.filter(r => {
    const badge = getFaelligkeitsBadge(r.faelligkeitsdatum, r.status);
    return badge === 'rot' || badge === 'gelb';
  }).length;
};

// Einheitliche Statusfarben für Rechnungen (analog zu getStatusStyle in RechnungDetailsSidebar).
const RECHNUNG_STATUS_FARBEN: Record<string, string> = {
  bezahlt: 'bg-green-50 text-green-600 border-green-100 hover:bg-green-100',
  offen: 'bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100',
  unklar: 'bg-purple-50 text-purple-600 border-purple-100 hover:bg-purple-100',
  klaerung: 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100',
};

export const getRechnungStatusBadgeClasses = (status: string | undefined | null): string => {
  return (
    RECHNUNG_STATUS_FARBEN[status?.toLowerCase() ?? ''] ??
    'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
  );
};

// ─── Mahnung / Zahlungserinnerung ──────────────────────────────────────────────
// Datenquelle: rechnungen.mahnstufe (int, >0 = gemahnt) ODER rechnungen_unklar.dokument_typ.

interface MahnFelder {
  mahnstufe?: number | null;
  dokument_typ?: string | null;
}

/**
 * Mahnung im Prüf-Pool, die zu keiner Rechnung gefunden hat (Ticket #421).
 * Sie bleibt bewusst liegen — aber benannt, damit niemand sie für eine offene
 * Forderung hält. Zugeordnete Mahnungen sind archiviert und stehen gar nicht mehr in der Liste.
 */
interface OffeneMahnFelder extends MahnFelder {
  is_unklar_source?: boolean;
  zugeordnete_rechnung_id?: string | null;
}

export const istMahnungOhneRechnung = (r: OffeneMahnFelder): boolean =>
  r.is_unklar_source === true && istMahnung(r) && !r.zugeordnete_rechnung_id;

export const istMahnung = (r: MahnFelder): boolean =>
  (r.mahnstufe ?? 0) > 0 ||
  r.dokument_typ === 'mahnung' ||
  r.dokument_typ === 'zahlungserinnerung';

// Kurzes Label für das Badge: „2. Mahnung", „Mahnung" oder „Zahlungserinnerung".
export const mahnLabel = (r: MahnFelder): string | null => {
  if (!istMahnung(r)) return null;
  const stufe = r.mahnstufe ?? 0;
  if (stufe > 1) return `${stufe}. Mahnung`;
  if (r.dokument_typ === 'zahlungserinnerung') return 'Zahlungserinnerung';
  return 'Mahnung';
};

// ─── Guthaben / Gutschrift ─────────────────────────────────────────────────────
// Negativer Betrag ODER dokument_typ 'gutschrift' = Geld zurück, keine Zahlungspflicht.

interface GuthabenFelder {
  brutto?: number | string | null;
  dokument_typ?: string | null;
}

export const istGuthaben = (r: GuthabenFelder): boolean =>
  (Number(r.brutto) || 0) < 0 || r.dokument_typ === 'gutschrift';

export const guthabenLabel = (r: GuthabenFelder): string | null =>
  istGuthaben(r) ? 'Guthaben' : null;

// ─── Betragsfarbe ───────────────────────────────────────────────────────────────
// Einheitliche Textfarbe für den Betrag in Liste + Panel: Guthaben grün, überfällige
// (unbezahlte) Beträge rot, alles andere (inkl. bezahlt) neutral.
interface BetragFarbeFelder {
  brutto?: number | string | null;
  faelligkeitsdatum?: string | null;
  status?: string | null;
}

export const getBetragFarbe = (r: BetragFarbeFelder): string => {
  if (istGuthaben(r)) return 'text-emerald-600';
  if (getFaelligkeitsBadge(r.faelligkeitsdatum, r.status ?? '') === 'rot') return 'text-red-600';
  return 'text-gray-900';
};

// ─── Richtung: Eingang (Verbindlichkeit) vs. Ausgang (eigene Forderung) ─────────
// Eigene Ausgangsrechnungen erkennt man am Aussteller Immobilienmanagement Schwede.
// Für Einträge ohne gespeicherte Richtung (u. a. KI-Pool/rechnungen_unklar) wird sie
// aus dem Aussteller abgeleitet — konsistent zur Backend-Logik (istEigenerAussteller).
const EIGEN_MARKER = ['immobilienmanagement schwede', 'marburger ring 32a'];

interface RichtungFelder {
  richtung?: 'eingang' | 'ausgang' | null;
  unternehmen?: string | null;
  zahlungsempfaenger?: string | null;
}

export const getRichtung = (r: RichtungFelder): 'eingang' | 'ausgang' => {
  if (r.richtung === 'ausgang' || r.richtung === 'eingang') return r.richtung;
  const aussteller = `${r.unternehmen ?? ''} ${r.zahlungsempfaenger ?? ''}`.toLowerCase();
  return EIGEN_MARKER.some((m) => aussteller.includes(m)) ? 'ausgang' : 'eingang';
};

export const RICHTUNG_LABEL: Record<'eingang' | 'ausgang', string> = {
  eingang: 'Eingehend',
  ausgang: 'Ausgehend',
};
